import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { DevAnalyticsDashboard, AnalyticsEntry, ScriptStatus } from "./DevAnalyticsDashboard";

// Debug mode - only logs in development
const DEBUG_ANALYTICS = import.meta.env.DEV;

// For dashboard display only - GA is initialized in index.html
const GA_MEASUREMENT_ID = "G-2MR2187D5N";

function debugLog(message: string, data?: Record<string, any>) {
  if (DEBUG_ANALYTICS) {
    console.log(
      `%c[Analytics] ${message}`,
      "color: #4CAF50; font-weight: bold;",
      data || ""
    );
  }
}

// Store for dashboard entries (module-level for trackEvent access)
let addEntryCallback: ((entry: AnalyticsEntry) => void) | null = null;

function createEntry(
  type: "pageview" | "event",
  data: AnalyticsEntry["data"]
): AnalyticsEntry {
  return {
    id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    type,
    timestamp: new Date(),
    data,
    gtagAvailable: typeof window !== "undefined" && typeof window.gtag === "function",
  };
}

// Track page view for Google Analytics (SPA navigation)
function trackGAPageView(
  path: string,
  title: string,
  onEntry?: (entry: AnalyticsEntry) => void
) {
  if (typeof window !== "undefined" && window.gtag) {
    debugLog("GA Page View", { path, title });
    window.gtag("event", "page_view", {
      page_path: path,
      page_title: title,
    });

    if (onEntry) {
      onEntry(createEntry("pageview", { path, title, source: "ga" }));
    }
  }
}

// Custom event tracking
export function trackEvent(
  eventName: string,
  eventParams?: Record<string, any>
) {
  debugLog("Custom Event", { eventName, eventParams });

  // Google Analytics
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", eventName, eventParams);
  }

  // Add to dashboard if available
  if (addEntryCallback) {
    addEntryCallback(createEntry("event", { eventName, eventParams }));
  }
}

// Analytics Provider Component
export function AnalyticsProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { analyticsSettings } = useSiteSettings();
  const [entries, setEntries] = useState<AnalyticsEntry[]>([]);
  const [scriptStatus, setScriptStatus] = useState<ScriptStatus>(() =>
    typeof window !== "undefined" && typeof window.gtag === "function"
      ? "loaded"
      : "loading"
  );

  const showDebugDashboard = analyticsSettings?.showDebugDashboard ?? false;

  const addEntry = useCallback((entry: AnalyticsEntry) => {
    setEntries((prev) => [...prev.slice(-49), entry]); // Keep last 50 entries
  }, []);

  // Register callback for trackEvent
  useEffect(() => {
    addEntryCallback = addEntry;
    return () => {
      addEntryCallback = null;
    };
  }, [addEntry]);

  const clearEntries = useCallback(() => {
    setEntries([]);
  }, []);

  // Send test ping event
  const sendTestPing = useCallback(() => {
    trackEvent("debug_ping", { timestamp: new Date().toISOString() });
  }, []);

  // Check if gtag became available (script loaded from index.html)
  useEffect(() => {
    if (scriptStatus === "loaded") return;
    
    const checkGtag = () => {
      if (typeof window.gtag === "function") {
        setScriptStatus("loaded");
      }
    };
    
    const timeout = setTimeout(checkGtag, 500);
    return () => clearTimeout(timeout);
  }, [scriptStatus]);

  // Track page views on route change
  useEffect(() => {
    const title = document.title;
    const path = location.pathname + location.search;

    // Small delay to ensure title is updated
    const timeout = setTimeout(() => {
      trackGAPageView(path, document.title || title, addEntry);
    }, 100);

    return () => clearTimeout(timeout);
  }, [location, addEntry]);

  return (
    <>
      {children}
      {showDebugDashboard && (
        <DevAnalyticsDashboard 
          entries={entries} 
          onClear={clearEntries}
          scriptStatus={scriptStatus}
          measurementId={GA_MEASUREMENT_ID}
          onTestPing={sendTestPing}
        />
      )}
    </>
  );
}

// Type declarations for window
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}
